import { useState, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Pagination,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import moment from "moment";
import { useApi } from "../hooks/useApi";

// tabla: nombre de la tabla en BD (ej: "mp_correctivos")
// idRegistro: ID del registro a auditar
export default function AuditoriaPanel({ tabla, idRegistro }) {
  const { get } = useApi();

  const [expanded, setExpanded] = useState(false);
  const [audits, setAudits] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetchAudits = useCallback(
    async (currentPage = 1) => {
      if (!idRegistro) return;
      setLoading(true);
      try {
        const result = await get(
          `/mp_auditoria?tabla=${tabla}&id_registro=${idRegistro}&page=${currentPage}&limit=10`
        );
        setAudits(result?.data ?? []);
        setTotal(result?.total ?? 0);
        setTotalPages(result?.totalPages ?? 1);
      } catch (err) {
        console.error("Error al cargar auditoría:", err);
      } finally {
        setLoading(false);
        setFetched(true);
      }
    },
    [tabla, idRegistro, get]
  );

  const handleAccordionChange = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded && !fetched) {
      fetchAudits(1);
    }
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchAudits(newPage);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleAccordionChange}
      disableGutters
      sx={{ mt: 2, boxShadow: "none", border: "1px solid", borderColor: "divider" }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle2">Historial de Auditoría</Typography>
          {total > 0 && (
            <Chip label={total} size="small" variant="outlined" color="default" />
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : audits.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ py: 2 }}
          >
            Sin registros de auditoría.
          </Typography>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Acción</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Datos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {audits.map((audit) => (
                  <TableRow key={audit.id} sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {moment(audit.fecha).format("DD/MM/YY HH:mm")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={audit.accion}
                        size="small"
                        color={
                          audit.accion === "DELETE"
                            ? "error"
                            : audit.accion === "INSERT"
                            ? "success"
                            : "info"
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{audit.usuario}</TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "monospace",
                          maxWidth: 260,
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={
                          typeof audit.datos === "object"
                            ? JSON.stringify(audit.datos)
                            : audit.datos
                        }
                      >
                        {typeof audit.datos === "object"
                          ? JSON.stringify(audit.datos)
                          : audit.datos}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
